/**
 * Product Personalization — preview image adapter
 *
 * On confirm: composite the personalized preview onto a canvas, upload to
 * Uploadcare, and contribute `_Personalization Preview Image` via
 * personalization:before-confirm. Removable — disable via PREVIEW_IMAGE_CONFIG
 * or delete this file + its CSS/script tag.
 *
 * Edit PREVIEW_IMAGE_CONFIG below for client keys / toggles.
 */

// ---------------------------------------------------------------------------
// Client config — edit here (no theme settings)
// ---------------------------------------------------------------------------
const PREVIEW_IMAGE_CONFIG = {
  enabled: false,
  uploadcarePublicKey: "624585767d057763d0b2",
  uploadEndpoint: "https://upload.uploadcare.com/base/",
  cdnBaseUrl: "https://ucarecdn.com",
  store: "auto",
  // JPEG + modest width keeps uploads ~50–150KB instead of ~500KB PNGs.
  outputMaxWidth: 1200,
  outputFormat: "jpeg",
  outputQuality: 0.72,
  loadingLabel: "Creating preview…",
  propertyKey: "_Personalization Preview Image",
  fallbackValue: "N/A",
};

(() => {
  if (!PREVIEW_IMAGE_CONFIG.enabled) return;

  const LOG_PREFIX = "product-personalization-preview-image:";
  const LOADING_ATTR = "data-personalization-preview-loading";
  const cacheByComponent = new WeakMap();

  document.addEventListener("personalization:before-confirm", (event) => {
    const component = event.detail?.component || event.target;
    if (!isPersonalizationComponent(component)) return;
    if (typeof event.detail?.waitUntil !== "function") return;

    event.detail.waitUntil(createPreviewProperty(component));
  });

  function isPersonalizationComponent(el) {
    return (
      el &&
      typeof el === "object" &&
      String(el.tagName || "").toLowerCase() === "product-personalization"
    );
  }

  async function createPreviewProperty(component) {
    const key = PREVIEW_IMAGE_CONFIG.propertyKey;
    const fallback = { properties: { [key]: PREVIEW_IMAGE_CONFIG.fallbackValue } };

    if (!hasUploadcareKey()) {
      console.error(
        LOG_PREFIX,
        "Missing uploadcarePublicKey in PREVIEW_IMAGE_CONFIG. Submitting fallback."
      );
      return fallback;
    }

    setLoading(component, true);
    try {
      const fingerprint = buildFingerprint(component);
      const cached = cacheByComponent.get(component);
      if (cached && cached.fingerprint === fingerprint && cached.url) {
        return { properties: { [key]: cached.url } };
      }

      const blob = await renderPreviewBlob(component);
      const url = await uploadBlob(blob, buildUploadFilename(component));
      cacheByComponent.set(component, { fingerprint, url });
      return { properties: { [key]: url } };
    } catch (err) {
      console.error(LOG_PREFIX, "Preview render/upload failed; submitting fallback.", err);
      return fallback;
    } finally {
      setLoading(component, false);
    }
  }

  function hasUploadcareKey() {
    const key = String(PREVIEW_IMAGE_CONFIG.uploadcarePublicKey || "").trim();
    return key && key !== "YOUR_PUBLIC_KEY";
  }

  function buildFingerprint(component) {
    const p = component.state?.personalization || {};
    const image = component.state?.product?.image || {};
    const font = safeGetFont(component, p.font);
    const color = safeGetColor(component, p.color);
    const displayFont = readDisplayFontSize(component);
    const styles = getComputedStyle(component);
    return [
      image.id || "",
      image.url || "",
      image.focalX,
      image.focalY,
      (p.text || "").trim(),
      font.id,
      font.family,
      font.weight,
      font.monogramFontStyle ? "monogram" : "",
      color.id,
      color.value,
      displayFont,
      styles.getPropertyValue("--personalization-zone-width").trim(),
      styles.getPropertyValue("--personalization-zone-height").trim(),
    ].join("|");
  }

  function safeGetFont(component, fontId) {
    if (typeof component._getFont === "function") {
      return component._getFont(fontId) || {};
    }
    return {};
  }

  function safeGetColor(component, colorId) {
    if (typeof component._getColor === "function") {
      return component._getColor(colorId) || {};
    }
    return {};
  }

  function readDisplayFontSize(component) {
    const raw = getComputedStyle(component)
      .getPropertyValue("--personalization-preview-font-size")
      .trim();
    const n = parseFloat(raw);
    return Number.isFinite(n) ? n : component.config?.maxFontSize || 48;
  }

  function absoluteUrl(url) {
    if (!url) return "";
    try {
      return new URL(url, window.location.href).href;
    } catch (_) {
      return url;
    }
  }

  function loadImageElement(url) {
    const src = absoluteUrl(url);
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = async () => {
        try {
          if (typeof img.decode === "function") await img.decode();
        } catch (_) {
          /* decode can fail on already-complete images; dimensions check below */
        }
        if (!img.naturalWidth || !img.naturalHeight) {
          reject(new Error(`Preview image has no dimensions: ${src}`));
          return;
        }
        resolve(img);
      };
      img.onerror = () =>
        reject(new Error(`Could not load preview image (CORS or 404): ${src}`));
      img.src = src;
    });
  }

  async function renderPreviewBlob(component) {
    const personalization = component.state?.personalization || {};
    const image = component.state?.product?.image || {};
    const font = safeGetFont(component, personalization.font);
    const color = safeGetColor(component, personalization.color);

    let text = String(personalization.text || "").trim();
    const capitalization = String(font.capitalization || "").toLowerCase();
    if (capitalization === "uppercase" || capitalization === "upper") {
      text = text.toUpperCase();
    }
    if (!text) throw new Error("No personalization text to render");
    if (!image.url) throw new Error("No preview image URL");

    const imageEl = await loadImageElement(image.url);

    if (document.fonts?.ready) {
      await document.fonts.ready.catch(() => {});
    }

    const naturalW = imageEl.naturalWidth;
    const naturalH = imageEl.naturalHeight;
    const maxW = PREVIEW_IMAGE_CONFIG.outputMaxWidth || 1600;
    const scale = Math.min(1, maxW / naturalW);
    const canvasW = Math.max(1, Math.round(naturalW * scale));
    const canvasH = Math.max(1, Math.round(naturalH * scale));

    const canvasEl = document.createElement("canvas");
    canvasEl.width = canvasW;
    canvasEl.height = canvasH;
    const ctx = canvasEl.getContext("2d");
    if (!ctx) throw new Error("Could not get 2d canvas context");

    // JPEG has no alpha — paint a white base so empty pixels aren't black.
    if (PREVIEW_IMAGE_CONFIG.outputFormat === "jpeg") {
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, canvasW, canvasH);
    }

    ctx.drawImage(imageEl, 0, 0, canvasW, canvasH);

    const displayW =
      component.els?.image?.clientWidth ||
      component.els?.preview?.clientWidth ||
      canvasW;
    const displayFont = readDisplayFontSize(component);
    const fontSize = Math.max(1, displayFont * (canvasW / Math.max(1, displayW)));

    const focalX = Number(image.focalX);
    const focalY = Number(image.focalY);
    const centerX = ((Number.isFinite(focalX) ? focalX : 50) / 100) * canvasW;
    const centerY = ((Number.isFinite(focalY) ? focalY : 50) / 100) * canvasH;

    const fontStyle = font.style || "normal";
    const fontWeight = font.weight || "400";
    const fontFamily = font.family || "Arial, Helvetica, sans-serif";
    const fill = color.value || "#1a1a1a";

    if (font.monogramFontStyle && text.length === 3) {
      drawMonogramText(ctx, text, centerX, centerY, fontSize, {
        fontStyle,
        fontWeight,
        fontFamily,
        fill,
      });
    } else {
      ctx.font = `${fontStyle} ${fontWeight} ${fontSize}px ${fontFamily}`;
      ctx.fillStyle = fill;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(text, centerX, centerY);
    }

    assertNotBlank(canvasEl);

    const mime =
      PREVIEW_IMAGE_CONFIG.outputFormat === "jpeg" ? "image/jpeg" : "image/png";
    const quality = clampQuality(PREVIEW_IMAGE_CONFIG.outputQuality);

    return new Promise((resolve, reject) => {
      if (typeof canvasEl.toBlob === "function") {
        canvasEl.toBlob(
          (result) => {
            if (result) resolve(result);
            else reject(new Error("Canvas toBlob returned empty result"));
          },
          mime,
          quality
        );
        return;
      }
      try {
        resolve(dataUrlToBlob(canvasEl.toDataURL(mime, quality)));
      } catch (err) {
        reject(err);
      }
    });
  }

  /**
   * Classic monogram layout: small · large · small, centered on the focal point.
   */
  function drawMonogramText(ctx, text, centerX, centerY, baseSize, options) {
    const chars = [...String(text)];
    if (chars.length !== 3) return;

    const sideScale = 0.72;
    const centerScale = 1.35;
    const sizes = [baseSize * sideScale, baseSize * centerScale, baseSize * sideScale];
    const { fontStyle, fontWeight, fontFamily, fill } = options;

    const widths = chars.map((ch, i) => {
      ctx.font = `${fontStyle} ${fontWeight} ${sizes[i]}px ${fontFamily}`;
      return ctx.measureText(ch).width;
    });
    const totalWidth = widths.reduce((sum, w) => sum + w, 0);

    let x = centerX - totalWidth / 2;
    ctx.fillStyle = fill;
    ctx.textAlign = "left";
    ctx.textBaseline = "middle";
    chars.forEach((ch, i) => {
      ctx.font = `${fontStyle} ${fontWeight} ${sizes[i]}px ${fontFamily}`;
      ctx.fillText(ch, x, centerY);
      x += widths[i];
    });
  }

  function clampQuality(value) {
    const n = Number(value);
    if (!Number.isFinite(n)) return 0.72;
    return Math.min(1, Math.max(0.1, n));
  }

  /** Guard against silently uploading an empty canvas. */
  function assertNotBlank(canvasEl) {
    const ctx = canvasEl.getContext("2d", { willReadFrequently: true });
    if (!ctx) throw new Error("Could not read canvas pixels");

    const w = canvasEl.width;
    const h = canvasEl.height;
    const sampleW = Math.min(64, w);
    const sampleH = Math.min(64, h);
    const points = [
      [0, 0],
      [Math.max(0, Math.floor(w / 2 - sampleW / 2)), Math.max(0, Math.floor(h / 2 - sampleH / 2))],
      [Math.max(0, w - sampleW), Math.max(0, h - sampleH)],
    ];

    for (const [sx, sy] of points) {
      let data;
      try {
        data = ctx.getImageData(sx, sy, sampleW, sampleH).data;
      } catch (err) {
        throw new Error(`Canvas is tainted, cannot export: ${err.message}`);
      }
      for (let i = 3; i < data.length; i += 4) {
        if (data[i] !== 0) return;
      }
    }
    throw new Error("Rendered canvas was blank");
  }

  function dataUrlToBlob(dataUrl) {
    const parts = dataUrl.split(",");
    const mime = parts[0].match(/:(.*?);/)?.[1] || "image/png";
    const binary = atob(parts[1] || "");
    const len = binary.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i += 1) bytes[i] = binary.charCodeAt(i);
    return new Blob([bytes], { type: mime });
  }

  async function uploadBlob(blob, filename) {
    const formData = new FormData();
    formData.append("UPLOADCARE_PUB_KEY", PREVIEW_IMAGE_CONFIG.uploadcarePublicKey);
    formData.append("UPLOADCARE_STORE", String(PREVIEW_IMAGE_CONFIG.store ?? "auto"));
    formData.append("file", blob, filename);

    const response = await fetch(PREVIEW_IMAGE_CONFIG.uploadEndpoint, {
      method: "POST",
      body: formData,
    });

    if (!response.ok) {
      const body = await response.text().catch(() => "");
      throw new Error(`Uploadcare upload failed (${response.status}): ${body}`);
    }

    const data = await response.json();
    const uuid = data?.file;
    if (!uuid) throw new Error("Uploadcare response missing file UUID");

    const base = String(PREVIEW_IMAGE_CONFIG.cdnBaseUrl || "https://ucarecdn.com").replace(
      /\/$/,
      ""
    );
    return `${base}/${uuid}/`;
  }

  /**
   * e.g. personalized-moon-pillow-emma-bold.png
   * Uploadcare keeps the original name in the CDN path after the UUID.
   */
  function buildUploadFilename(component) {
    const ext = PREVIEW_IMAGE_CONFIG.outputFormat === "jpeg" ? "jpg" : "png";
    const handle = slugify(component.state?.product?.handle || "product");
    const text = slugify(component.state?.personalization?.text || "text");
    const font = slugify(safeGetFont(component, component.state?.personalization?.font).label || "");
    const parts = ["personalized", handle, text];
    if (font) parts.push(font);
    return `${parts.filter(Boolean).join("-").slice(0, 120)}.${ext}`;
  }

  function slugify(value) {
    return String(value || "")
      .trim()
      .toLowerCase()
      .normalize("NFKD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 40);
  }

  function setLoading(component, isLoading) {
    const dialog = component.els?.dialog || component.querySelector("[data-personalization-dialog]");
    const confirmBtn =
      component.els?.confirmBtn ||
      component.querySelector("[data-personalization-confirm]");
    const cancelBtn = component.querySelector("[data-personalization-cancel]");
    const closeBtn = component.querySelector("[data-personalization-close]");

    if (isLoading) {
      component.setAttribute(LOADING_ATTR, "true");
      component.setAttribute("aria-busy", "true");
      if (dialog) {
        dialog.setAttribute(LOADING_ATTR, "true");
        dialog.setAttribute("aria-busy", "true");
      }
      ensureLoadingEl(component, dialog);
    } else {
      component.removeAttribute(LOADING_ATTR);
      component.removeAttribute("aria-busy");
      if (dialog) {
        dialog.removeAttribute(LOADING_ATTR);
        dialog.removeAttribute("aria-busy");
      }
      const status = component.querySelector("[data-personalization-preview-status]");
      if (status) status.hidden = true;
    }

    [confirmBtn, cancelBtn, closeBtn].forEach((btn) => {
      if (!btn) return;
      if (isLoading) {
        if (!btn.hasAttribute("data-preview-was-disabled")) {
          btn.setAttribute("data-preview-was-disabled", btn.disabled ? "1" : "0");
        }
        btn.disabled = true;
      } else if (btn.hasAttribute("data-preview-was-disabled")) {
        btn.disabled = btn.getAttribute("data-preview-was-disabled") === "1";
        btn.removeAttribute("data-preview-was-disabled");
      } else {
        btn.disabled = false;
      }
    });

    if (confirmBtn) {
      if (isLoading) {
        if (!confirmBtn.hasAttribute("data-preview-original-label")) {
          confirmBtn.setAttribute(
            "data-preview-original-label",
            confirmBtn.textContent || ""
          );
        }
        confirmBtn.textContent = PREVIEW_IMAGE_CONFIG.loadingLabel;
      } else if (confirmBtn.hasAttribute("data-preview-original-label")) {
        confirmBtn.textContent =
          confirmBtn.getAttribute("data-preview-original-label") || "";
        confirmBtn.removeAttribute("data-preview-original-label");
      }
    }
  }

  function ensureLoadingEl(component, dialog) {
    const host =
      (dialog && dialog.querySelector(".product-personalization__dialog-inner")) ||
      dialog ||
      component;
    let status = host.querySelector("[data-personalization-preview-status]");
    if (!status) {
      status = document.createElement("div");
      status.className = "product-personalization__preview-status";
      status.setAttribute("data-personalization-preview-status", "");
      status.setAttribute("role", "status");
      status.setAttribute("aria-live", "polite");
      host.appendChild(status);
    }
    status.hidden = false;
    status.textContent = PREVIEW_IMAGE_CONFIG.loadingLabel;
  }
})();
